/**
 * components/object/ObjectRevisions.jsx — Iter 51.
 *
 * Pluggable "Revisions" tab for the unified ObjectInspector. Lists every
 * revision review touching the focused object, with impacted-field diff
 * snippets. Tapping a revision dispatches a Jump to the sheet anchor so
 * the field user lands on the drawing at the right place.
 *
 * Calls /api/objects/{type}/{id}/revision_impacts (Iter 51 endpoint).
 * Returns null with calm zero-state when no revisions touch the object.
 */
import React, { useEffect, useState } from "react";
import { GitBranch, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { fetchRevisionImpacts } from "@/lib/objectIntel";
import { dispatchJump } from "@/lib/jumpTo";

export default function ObjectRevisions({ objectType, objectId, refreshKey }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!objectType || !objectId) return;
    let cancel = false;
    setLoading(true); setErr(null);
    fetchRevisionImpacts(objectType, objectId)
      .then((r) => { if (!cancel) { setRows(r); setLoading(false); } })
      .catch((e) => { if (!cancel) { setErr(e?.message || "load failed"); setLoading(false); } });
    return () => { cancel = true; };
  }, [objectType, objectId, refreshKey]);

  if (loading) {
    return (
      <div className="px-4 py-6 text-[12px] text-[var(--fop-text-3)] flex items-center gap-1.5"
            data-testid="object-revisions-loading">
        <Loader2 size={12} className="animate-spin" /> Loading revisions…
      </div>
    );
  }

  if (err) {
    return (
      <div className="px-4 py-3 text-[12px] text-[var(--fop-danger,var(--st-escalated))] flex items-center gap-1.5"
            data-testid="object-revisions-error">
        <AlertTriangle size={12} /> {err}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-[12px] text-[var(--fop-text-3)]"
            data-testid="object-revisions-empty">
        No revisions have touched this object yet.
      </div>
    );
  }

  return (
    <ul className="space-y-3 px-4 py-3" data-testid="object-revisions-list"
         data-count={rows.length}>
      {rows.map((r) => (
        <RevisionRow key={r.review_id} row={r} />
      ))}
    </ul>
  );
}

function RevisionRow({ row }) {
  const onJump = () => {
    if (!row.sheet_id) return;
    dispatchJump({
      object_type: "revision",
      object_id: row.sheet_id,
      sheet_id: row.sheet_id,
      label: `${row.sheet_number || ""} REV ${row.current_revision || ""}`.trim(),
    });
  };
  return (
    <li
      className="rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface-2)] px-3 py-2 cursor-pointer hover:border-[var(--fop-text-3)] transition-colors"
      onClick={onJump}
      data-testid={`object-revision-row-${row.review_id}`}
      data-sheet-id={row.sheet_id}
    >
      <div className="flex items-center gap-2 mb-1">
        <GitBranch size={11} className="text-[var(--fop-primary)] shrink-0" />
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)]">
          {row.sheet_number || row.sheet_id?.slice(-6)}
        </div>
        <span className="text-[var(--fop-text-3)]">·</span>
        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          REV <b className="text-[var(--fop-text-2)]">{row.prior_revision ?? "—"}</b>
          <ArrowRight size={9} className="inline mx-1 -mt-0.5" />
          <b className="text-[var(--fop-primary)]">{row.current_revision ?? "—"}</b>
        </span>
      </div>
      {row.summary && (
        <div className="text-[11px] text-[var(--fop-text-2)] leading-snug mb-1">
          {row.summary}
        </div>
      )}
      {row.impacted_fields?.length > 0 && (
        <ul className="space-y-0.5 mt-1">
          {row.impacted_fields.slice(0, 6).map((f, i) => (
            <li key={i} className="text-[10px] font-mono text-[var(--fop-text-3)]"
                 data-testid={`object-revision-field-${f.key || i}`}>
              {f.field || f.key}:{" "}
              <span className="line-through opacity-60">{fmtVal(f.prev, f.unit)}</span>
              {" → "}
              <b className="text-[var(--fop-primary)]">{fmtVal(f.current, f.unit)}</b>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function fmtVal(v, unit) {
  if (v == null) return "—";
  if (typeof v === "number") return `${v}${unit ? ` ${unit}` : ""}`;
  return String(v);
}
